import React, { useState } from 'react';
import { Building, Calendar, MapPin, CheckCircle, ChevronDown, ChevronUp, Tag, Cpu, TrendingUp, Users, BookOpen } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const Experience = ({ experience }) => {
  const { t } = useLanguage();
  const [expandedExperience, setExpandedExperience] = useState({});
  const { ref: timelineRef, isVisible: timelineVisible } = useScrollReveal();
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal();

  // Helper for translated experience text with fallback to raw data
  const jt = (job, field) => {
    if (!job.translationKey) return job[field];
    const key = `experience.jobs.${job.translationKey}.${field}`;
    const val = t(key);
    return val === key ? job[field] : val;
  };

  // Helper for translated achievements array
  const getAchievements = (job) => {
    if (!job.translationKey) return job.achievements;
    const key = `experience.jobs.${job.translationKey}.achievements`;
    const val = t(key);
    return Array.isArray(val) ? val : job.achievements;
  };

  const toggleExperience = (index) => {
    setExpandedExperience(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <section id="experience" className="py-20 md:py-32 bg-muted dark:bg-muted">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-sm font-medium mb-4 tracking-wide uppercase">
            {t('experience.title')}
          </span>
          <h2 className="text-3xl md:text-5xl font-light text-foreground mb-4">
            {t('experience.title')}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-teal-500 mx-auto rounded-full mb-8" />
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('experience.subtitle')}
          </p>
        </div>

        {/* Experience timeline */}
        <div ref={timelineRef} className={`relative reveal-up ${timelineVisible ? 'visible' : ''}`}>
          {/* Timeline line */}
          <div className="absolute left-8 md:left-1/2 md:-ml-0.5 w-0.5 h-full bg-gradient-to-b from-blue-600 via-teal-500 to-blue-600/30"></div>

          <div className="space-y-12">
            {experience.map((job, index) => (
              <div
                key={job.id}
                className={`relative flex items-start ${
                  index % 2 === 0 ? 'md:flex-row-reverse' : ''
                }`}
              >
                {/* Timeline dot */}
                <div className={`absolute left-6 md:left-1/2 md:-ml-3.5 w-7 h-7 rounded-full z-10 border-4 border-white dark:border-gray-900 shadow-md ${
                  job.status === 'Current'
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                    : 'bg-gradient-to-br from-blue-500 to-blue-600'
                }`}>
                  {job.status === 'Current' && (
                    <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-40" />
                  )}
                </div>

                {/* Content card */}
                <div className={`ml-16 md:ml-0 ${
                  index % 2 === 0 ? 'md:mr-8 md:text-right' : 'md:ml-8'
                } md:w-1/2`}>
                  <Card className={`p-6 bg-card dark:bg-card border border-border/50 hover:shadow-lg hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300 border-t-2 ${
                    job.status === 'Current' ? 'border-t-green-500' : 'border-t-blue-500'
                  }`}>
                    {/* Company and role header */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold text-foreground">
                          {jt(job, 'position')}
                        </h3>
                        {job.status && (
                          <Badge
                            variant="secondary"
                            className={`${
                              job.status === 'Current'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {job.status === 'Current' ? t('experience.current') : t('experience.completed')}
                          </Badge>
                        )}
                      </div>

                      <div className="flex items-center text-blue-600 dark:text-blue-400 font-medium mb-2">
                        <Building className="w-4 h-4 mr-2" />
                        {job.company}
                      </div>

                      <div className="flex items-center text-muted-foreground text-sm space-x-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {jt(job, 'duration')}
                        </div>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          {jt(job, 'location')}
                        </div>
                      </div>
                    </div>

                    {/* Key achievements preview */}
                    <div className="space-y-3 mb-4">
                      {getAchievements(job).slice(0, 2).map((achievement, achIndex) => (
                        <div
                          key={achIndex}
                          className="flex items-start text-muted-foreground"
                        >
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="leading-relaxed text-sm">{achievement}</span>
                        </div>
                      ))}
                      {getAchievements(job).length > 2 && !expandedExperience[index] && (
                        <p className="text-sm text-muted-foreground/70 italic">
                          +{getAchievements(job).length - 2} {t('experience.moreAchievements')}
                        </p>
                      )}
                    </div>

                    {/* Expand button */}
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExperience(index)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        {expandedExperience[index] ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            {t('experience.showLess')}
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            {t('experience.showMore')}
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Expanded content */}
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedExperience[index] ? 'max-h-[2000px] opacity-100 mt-6' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="pt-6 border-t border-border">
                        {job.description && (
                          <div className="mb-6">
                            <p className="text-muted-foreground leading-relaxed">{jt(job, 'description')}</p>
                          </div>
                        )}

                        <div className="space-y-3 mb-6">
                          <h4 className="font-medium text-foreground mb-3">{t('experience.keyAchievements')}:</h4>
                          {getAchievements(job).map((achievement, achIndex) => (
                            <div key={achIndex} className="flex items-start text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                              <span className="leading-relaxed">{achievement}</span>
                            </div>
                          ))}
                        </div>

                        {job.tags && job.tags.length > 0 && (
                          <div>
                            <div className="flex items-center mb-3">
                              <Tag className="w-4 h-4 text-muted-foreground mr-2" />
                              <span className="text-sm font-medium text-foreground">{t('experience.technologiesSkills')}:</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {job.tags.map((tag, tagIndex) => (
                                <Badge
                                  key={tagIndex}
                                  variant="secondary"
                                  className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-300 border-0 hover:bg-blue-500/20 transition-colors"
                                >
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary stats */}
        <div ref={statsRef} className={`mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 text-center reveal-up ${statsVisible ? 'visible' : ''}`}>
          {[
            { icon: Cpu, value: '10K+', label: t('experience.stats.aiModels'), sub: t('experience.stats.yoloModels'), gradient: 'from-blue-500 to-blue-600', color: 'text-blue-600 dark:text-blue-400' },
            { icon: TrendingUp, value: '25%', label: t('experience.stats.seoImprovement'), sub: t('experience.stats.visibilityBoost'), gradient: 'from-green-500 to-green-600', color: 'text-green-600 dark:text-green-400' },
            { icon: Users, value: '50+', label: t('experience.stats.projectsDelivered'), sub: t('experience.stats.freelanceCorporate'), gradient: 'from-purple-500 to-purple-600', color: 'text-purple-600 dark:text-purple-400' },
            { icon: BookOpen, value: '100+', label: t('experience.stats.studentsTaught'), sub: t('experience.stats.programmingMath'), gradient: 'from-orange-500 to-orange-600', color: 'text-orange-600 dark:text-orange-400' },
          ].map((stat, i) => {
            const IconComp = stat.icon;
            return (
              <div
                key={i}
                className="p-6 bg-card dark:bg-card rounded-xl border border-border/50 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all duration-300"
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center mx-auto mb-3 shadow-md`}>
                  <IconComp className="w-6 h-6 text-white" />
                </div>
                <div className={`text-3xl font-bold ${stat.color} mb-2`}>{stat.value}</div>
                <div className="text-foreground text-sm font-medium">{stat.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{stat.sub}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Experience;
